/**
 * Turning composer text into a signable event template.
 *
 * Kept as a pure function so the tag rules are testable without a signer, a
 * relay, or React. Every rule here is a protocol requirement that is invisible
 * in the UI but load-bearing for other clients:
 *
 *  - `t` tags are what make a hashtag findable. Text containing `#nostr` with no
 *    `t` tag is not in that hashtag's feed anywhere on the network.
 *  - `p` tags are what make a mention *notify* the person mentioned. Without
 *    them a `nostr:npub…` in the body is decoration.
 *  - `e` tags are the reply's position in its thread, and getting the
 *    root/reply markers wrong is what makes a reply appear as a top-level note
 *    in other clients.
 */

import {
  type EventTemplate,
  type Hex32,
  Kind,
  type NostrEvent,
  rootAndReplyIds,
  tokenizeContent,
} from "@setu/protocol";
import { noteReference } from "../notes/noteLink";
import {
  LIKE,
  type ReactionChoice,
  reactionContent,
} from "../notes/reactionChoice";

export interface ReplyTarget {
  /** The note being replied to. */
  readonly parent: NostrEvent;
  /** Relay hint to advertise for the parent, if known. */
  readonly relayHint?: string;
}

export interface QuoteTarget {
  /** The note being quoted. */
  readonly target: NostrEvent;
  /** A relay we have actually seen it on, so the reference resolves elsewhere. */
  readonly relayHint?: string;
}

export interface BuildNoteInput {
  readonly content: string;
  readonly reply?: ReplyTarget;
  /**
   * The note this one quotes (NIP-18).
   *
   * Separate from `reply` because a quote is explicitly *not* a thread edge: filing
   * it as one puts a quote of a stranger's post inside that stranger's reply
   * thread. Both may be set — quoting something in a reply is a normal thing to
   * do — and they produce independent tags.
   */
  readonly quote?: QuoteTarget;
  /** Extra pubkeys to notify, e.g. everyone already in the thread. */
  readonly notify?: readonly Hex32[];
  /** NIP-36 warning reason. An empty string still means "warn". */
  readonly contentWarning?: string;
  /**
   * NIP-40 expiration, as a unix timestamp in seconds.
   *
   * A *request*, in exactly the sense a NIP-09 deletion is one: relays that
   * implement NIP-40 stop serving the event after this time, relays that do not
   * keep serving it, and anyone who already fetched it keeps their copy. The UI
   * that sets this has to say so — an "expires in 1 hour" control that reads as a
   * guarantee is worse than no control, because it invites the reader to post
   * something they would not otherwise post.
   */
  readonly expiresAt?: number;
  /**
   * Tags the caller has already built — `imeta` for attached media, today.
   *
   * Appended rather than merged, and deliberately last: a caller cannot use this
   * to overwrite the `e`/`p` tags that place the note in its thread, because
   * getting those wrong breaks threading for everyone who reads the note.
   */
  readonly extraTags?: readonly (readonly string[])[];
  readonly createdAt?: number;
}

/** Lowercased, deduped hashtags found in the body. */
function hashtagsIn(content: string): string[] {
  const seen = new Set<string>();
  for (const token of tokenizeContent(content)) {
    if (token.type === "hashtag") seen.add(token.tag.toLowerCase());
  }
  return [...seen];
}

/** Pubkeys referenced by `nostr:npub…`/`nostr:nprofile…` mentions in the body. */
function mentionedPubkeys(content: string): Hex32[] {
  const seen = new Set<Hex32>();
  for (const token of tokenizeContent(content)) {
    if (token.type !== "mention") continue;
    const ref = token.entity;
    if (ref.type === "npub" || ref.type === "nprofile") seen.add(ref.pubkey);
  }
  return [...seen];
}

/**
 * Build the `e` tags for a reply, per NIP-10's marked scheme.
 *
 * The parent's *own* thread position decides the root: replying to a reply keeps
 * that reply's root, while replying to a top-level note makes that note the root.
 * Emitting the parent as root in the second case is the common bug — it flattens
 * every sub-thread into one.
 */
function replyTags(reply: ReplyTarget): string[][] {
  const { parent, relayHint } = reply;
  const hint = relayHint ?? "";
  const parentRefs = rootAndReplyIds(parent);
  const rootId = parentRefs.root ?? parent.id;

  if (rootId === parent.id) {
    // Parent is the thread root: one tag, marked both ways per NIP-10.
    return [["e", rootId, hint, "root"]];
  }
  return [
    ["e", rootId, hint, "root"],
    ["e", parent.id, hint, "reply"],
  ];
}

/**
 * The body a quote actually publishes.
 *
 * The `nostr:nevent…` reference in the content is the primary signal — it is what
 * a renderer walks to draw the embedded card, and a `q` tag alone renders as
 * nothing in most clients. So the reference is appended rather than left to the
 * author, because a quote composer that produced a bare comment would silently
 * publish a note with no visible quote in it.
 *
 * Appended only when absent, matched on the bare entity so a reference the author
 * pasted themselves counts whether or not they typed the `nostr:` scheme. Two
 * copies of the same reference draw the quoted note twice.
 */
function withQuoteReference(content: string, quote: QuoteTarget): string {
  const reference = noteReference({
    id: quote.target.id,
    author: quote.target.pubkey,
    kind: quote.target.kind,
    ...(quote.relayHint ? { relayHint: quote.relayHint } : {}),
  });
  // Undefined only for an id that is not a valid event id, which cannot be
  // encoded and so cannot be quoted. The `q` tag still names it.
  if (reference === undefined) return content;
  if (content.includes(reference.slice("nostr:".length))) return content;
  return content === "" ? reference : `${content}\n\n${reference}`;
}

/**
 * Assemble the event template.
 *
 * Tag order is deliberate: `e` tags first (thread position), then `q`, then `p`,
 * then `t`. Nothing in the protocol requires it, but a stable order makes events
 * diffable and tests meaningful.
 */
export function buildNote(input: BuildNoteInput): EventTemplate {
  const trimmed = input.content.trim();
  const content = input.quote
    ? withQuoteReference(trimmed, input.quote)
    : trimmed;
  const tags: string[][] = [];

  if (input.reply) tags.push(...replyTags(input.reply));

  // `["q", id, relay, pubkey]` — the queryable half of a quote, and the only half
  // a client that does not walk content can see. The pubkey is what lets a reader
  // find the quoted author's relays without first holding the event.
  if (input.quote) {
    tags.push([
      "q",
      input.quote.target.id,
      input.quote.relayHint ?? "",
      input.quote.target.pubkey,
    ]);
  }

  // Notify the parent's author, everyone the parent notified, anyone mentioned
  // in the body, and any explicit extras — deduped, and never ourselves twice.
  const people = new Set<Hex32>();
  if (input.reply) {
    people.add(input.reply.parent.pubkey);
    for (const tag of input.reply.parent.tags) {
      if (tag[0] === "p" && tag[1]) people.add(tag[1]);
    }
  }
  // The quoted author, so being quoted is a notification rather than something
  // you find out about. Only the author: the quoted note's own `p` tags are its
  // thread's business, and pulling them in would notify a dozen strangers that
  // someone quoted a note they were mentioned in.
  if (input.quote) people.add(input.quote.target.pubkey);
  for (const pubkey of mentionedPubkeys(content)) people.add(pubkey);
  for (const pubkey of input.notify ?? []) people.add(pubkey);
  for (const pubkey of people) tags.push(["p", pubkey]);

  for (const tag of hashtagsIn(content)) tags.push(["t", tag]);

  if (input.contentWarning !== undefined) {
    tags.push(
      input.contentWarning
        ? ["content-warning", input.contentWarning]
        : ["content-warning"],
    );
  }

  // Whole seconds, and only when it is actually in the future: a timestamp in the
  // past asks every relay to drop the event on arrival, which publishes a note that
  // is gone before anyone can read it.
  if (input.expiresAt !== undefined) {
    const expiry = Math.floor(input.expiresAt);
    if (expiry > Math.floor(Date.now() / 1000)) {
      tags.push(["expiration", String(expiry)]);
    }
  }

  // Last, so nothing a caller passes can displace the threading tags above.
  for (const tag of input.extraTags ?? []) tags.push([...tag]);

  return {
    kind: Kind.ShortTextNote,
    content,
    tags,
    ...(input.createdAt !== undefined ? { created_at: input.createdAt } : {}),
  };
}

/**
 * Reaction event (NIP-25), defaulting to a plain like.
 *
 * A custom emoji reaction is two halves that have to agree: the content is the
 * `:shortcode:` and a NIP-30 `emoji` tag on the *same event* maps that shortcode to
 * an image. Publishing the shortcode without the tag is the common bug, and it is
 * invisible to the author — their own client resolves the code from the emoji set
 * they picked it from, while every reader sees the literal text `:shortcode:`.
 * `ReactionChoice` makes the pair unsplittable, so that cannot be built here.
 */
export function buildReaction(
  target: NostrEvent,
  choice: ReactionChoice = LIKE,
): EventTemplate {
  return {
    kind: Kind.Reaction,
    content: reactionContent(choice),
    tags: [
      ["e", target.id],
      ["p", target.pubkey],
      ["k", String(target.kind)],
      ...(choice.kind === "custom"
        ? [["emoji", choice.shortcode, choice.url]]
        : []),
    ],
  };
}

/**
 * Deletion request (NIP-09).
 *
 * Two things this cannot do, and neither is a gap to work around:
 *
 *  - It cannot delete someone else's event. A relay is expected to ignore a
 *    kind-5 whose author is not the target's author, so the *caller* must pass
 *    only events it signed. `useNoteActions` enforces that by finding the events
 *    to delete with `authors: [me]` rather than trusting a view model.
 *  - It cannot guarantee the event is gone. A deletion is a request: relays that
 *    honour it drop the event, relays that do not keep serving it. Setu's own
 *    store treats a tombstone as insert-blocking, so locally it stays gone even
 *    if a relay hands it back — but "deleted" on the network is a hope, not a
 *    fact, and no UI here should claim otherwise.
 *
 * The `k` tags are what let a relay apply the request without holding the target:
 * without them it must have the event to know its kind, and a relay that pruned
 * it cannot enforce the deletion at all.
 */
export function buildDeletion(
  targets: readonly NostrEvent[],
  reason = "",
): EventTemplate {
  const tags: string[][] = [];
  for (const target of targets) tags.push(["e", target.id]);
  for (const kind of new Set(targets.map((target) => target.kind))) {
    tags.push(["k", String(kind)]);
  }
  return { kind: Kind.EventDeletion, content: reason, tags };
}

/** Repost event (NIP-18). Kind 6 for notes, 16 for anything else. */
export function buildRepost(target: NostrEvent, relayHint = ""): EventTemplate {
  const isNote = target.kind === Kind.ShortTextNote;
  return {
    kind: isNote ? Kind.Repost : Kind.GenericRepost,
    // NIP-18 allows embedding the reposted event so clients can render it
    // without a second fetch.
    content: JSON.stringify(target),
    tags: [
      ["e", target.id, relayHint],
      ["p", target.pubkey],
      ...(isNote ? [] : [["k", String(target.kind)]]),
    ],
  };
}
